import { NeedsActionDto } from '@act/shared/data-transfer-objects';
import { Permission } from '@act/shared/authorization';
import UpdateIcon from '@mui/icons-material/Update';
import DeleteIcon from '@mui/icons-material/Delete';
import { Box, Typography, IconButton } from '@mui/material';
import { DateTime } from 'luxon';
import { useMemo, useEffect } from 'react';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { userAppSelectors } from 'redux/slices/usersSlice.slice';
import { actingUserAppSelectors } from 'redux/slices/actingUserSlice.slice';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { needRequests } from 'redux/requests/need.requests';
import { useAppDispatch } from 'redux/storeHooks';
import { getMemberNeeds, needsSelectors } from 'redux/slices/needs.slice';
import useConfirmationAlertDialog from 'react-components/dialog-forms/hooks/useConfirmationAlertDialog.hook';
import { CollapsibleHistoryCard } from './CollapsibleHistoryCard';
import { getHistoryEntryDateLine } from './HistoryCardHelpers';
import './need-history.styles.scss';
import { StyledHistoryEntryDescription } from './HistoryDisplay';
import { IdentificationBadgeIcon, StrategyIcon } from '@phosphor-icons/react';
import { useToast } from 'react-components/app/ReusableToast';

interface NeedActionHistoryEntryProps {
  action: NeedsActionDto;
  detailMode?: boolean;
  editMode?: boolean;
  setEdit?: (action: NeedsActionDto) => void;
  needId?: number;
}

export const NeedActionHistoryEntry = ({
  action,
  detailMode,
  editMode,
  setEdit,
  needId,
}: NeedActionHistoryEntryProps) => {
  const toast = useToast();
  const dispatch = useAppDispatch();
  const userMap = userAppSelectors.useSelectUsersMap();
  const needMetadata = metaSliceSelectors.useSelectNeedsMetadata();
  const actingUser = actingUserAppSelectors.useSelectActingUser();
  const openConfirmationDialog = useConfirmationAlertDialog();
  const activities = needMetadata?.data?.activities;
  const need = needId ? needsSelectors.useSelectNeedById(needId) : null;

  const [deleteResult, isDeleting, deleteAction] = useApiCall(
    needRequests.deleteAccidentallyCreatedAction
  );

  const hasAdminPermission = actingUserAppSelectors.useUserHasPermission(
    Permission.CAN_ACCESS_ADMIN_TOOLS
  );

  // Check if user can delete this action
  const canDeleteAction = useMemo(() => {
    if (!actingUser || !action) return false;

    // Admins can always delete
    if (hasAdminPermission) return true;

    // Guides can only delete actions they created today with no subsequent actions
    if (action.creatorId === actingUser.id) {
      const actionDate = DateTime.fromMillis(action.createdAt);
      const today = DateTime.now().startOf('day');
      const isToday = actionDate >= today;
      const hasNoSubsequentActions =
        !action.subsequentActions || action.subsequentActions.length === 0;
      return isToday && hasNoSubsequentActions;
    }

    return false;
  }, [actingUser, action, hasAdminPermission]);

  // Handle delete result
  useEffect(() => {
    if (deleteResult !== null && !isDeleting && need) {
      toast('Action deleted successfully');
      dispatch(getMemberNeeds({ params: { memberId: need.memberId } }));
    }
  }, [deleteResult, isDeleting, need, toast, dispatch]);

  const handleDelete = async () => {
    const confirmed = await openConfirmationDialog({
      title: 'Delete Action',
      message:
        'Are you sure you want to delete this action? This will also delete related history entries. This action cannot be undone.',
      confirmationText: 'Delete',
      cancelText: 'Cancel',
      confirmButtonColor: 'error',
      icon: <DeleteIcon />,
      iconColor: 'error',
    });

    if (confirmed) {
      deleteAction({
        params: { needId: need?.id, actionId: action.id },
      });
    }
  };
  const [latestAction, restOfActions] = useMemo(() => {
    const allActions = [action, ...(action.subsequentActions || [])];
    allActions.sort((a, b) => b.createdAt - a.createdAt);
    return [allActions[0], detailMode ? allActions.slice(1) : []];
  }, [action, detailMode]);

  function getActionTitle(action: NeedsActionDto): string {
    const relevantAction = action?.subsequentActions?.length
      ? [...action.subsequentActions].sort(
          (a, b) => b.createdAt - a.createdAt
        )[0]
      : action;

    return activities?.find(i => i.id === relevantAction?.activityId)?.label;
  }

  const actionTitle = getActionTitle(action);
  const hasSubsequentActions = !!action.subsequentActions?.length;

  const uniqueResourceCount = useMemo(() => {
    // Collect all resourceReferrals from main action and subsequentActions
    const allResourceReferrals = [
      ...(action.resourceReferrals || []),
      ...(action.subsequentActions || []).flatMap(
        sa => sa.resourceReferrals || []
      ),
    ];
    // Get unique resources by id
    const uniqueResourceIds = new Set(
      allResourceReferrals.map(ref => ref.id).filter(id => id !== undefined)
    );

    return uniqueResourceIds.size;
  }, [action]);

  return (
    <>
      <CollapsibleHistoryCard
        title={actionTitle}
        subtitle={getHistoryEntryDateLine(
          DateTime.fromISO(latestAction.date).toMillis(),
          userMap && userMap[latestAction.creatorId]?.name
        )}
        icon={
          hasSubsequentActions ? (
            <UpdateIcon color="primary" />
          ) : (
            <StrategyIcon />
          )
        }
        expandedContent={
          <Box display="flex" flexDirection="column" gap="16px">
            <StyledHistoryEntryDescription
              dateAndUser={getHistoryEntryDateLine(
                latestAction.createdAt,
                userMap && userMap[latestAction.creatorId]?.name
              )}
              description={latestAction.description}
            ></StyledHistoryEntryDescription>
            {uniqueResourceCount > 0 && (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <IdentificationBadgeIcon size={16} />
                <Typography variant="bodySmall" className="resource-count">
                  {uniqueResourceCount} resource
                  {uniqueResourceCount !== 1 ? 's' : ''} referred
                </Typography>
              </div>
            )}
            {!!restOfActions.length && (
              <>
                {restOfActions.map(a => (
                  <div key={a.id}>
                    <StyledHistoryEntryDescription
                      dateAndUser={getHistoryEntryDateLine(
                        a.createdAt,
                        userMap && userMap[a.creatorId]?.name
                      )}
                      description={a.description}
                    ></StyledHistoryEntryDescription>
                  </div>
                ))}
              </>
            )}
            {canDeleteAction && editMode && (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  marginTop: '8px',
                }}
              >
                <IconButton
                  size="small"
                  onClick={handleDelete}
                  disabled={isDeleting}
                  aria-label="Delete action"
                  sx={{ color: 'error.main' }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </Box>
            )}
          </Box>
        }
        showEditButton={editMode}
        onEdit={() => {
          setEdit?.(action);
        }}
      />
    </>
  );
};
